import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Info, Pause, Play, Timer } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  STS_BORG_LABELS,
  STS_BORG_VALUES,
  STS_REFERENCE_CHAIR_HEIGHT_CM,
  formatBorgValue,
  normalizeStsRepetitionPayload,
  type StsBorgVitals,
  type StsRepetitionAttemptPayload,
  type StsRepetitionResultPayload,
} from '../sts/stsDomain';
import AssessmentWeightAdjustmentField from '../components/AssessmentWeightAdjustmentField';
import { normalizePatientHeightCm, normalizePatientWeightKg, toFiniteNumber } from '../utils/patientMetrics';

type STSPatientContext = {
  birthDate?: string | null;
  ageYears?: number | null;
  sex?: string | null;
  weightKg?: number | null;
  heightCm?: number | null;
  identifier?: string | null;
};

interface STSRepeatedAttemptTestBaseProps {
  testKey: 'STS_30S' | 'STS_1MIN';
  title: string;
  description: string;
  durationSeconds: number;
  recommendedRestSeconds: number;
  saveLabel: string;
  allowFinalizeAfterFirstAttempt: boolean;
  initial?: StsRepetitionResultPayload | null;
  patientContext?: STSPatientContext;
  onCancel?: () => void;
  onDraftAttempt?: (payload: StsRepetitionResultPayload) => Promise<boolean | void> | boolean | void;
  onNext: (result: StsRepetitionResultPayload) => void;
}

const buildEmptyVitals = (): StsBorgVitals => ({
  borgDyspneaPre: null,
  borgLegPre: null,
  spo2Pre: null,
  hrPre: null,
  borgDyspneaPost: null,
  borgLegPost: null,
  spo2Post: null,
  hrPost: null,
});

const buildVitalsFromAttempt = (attempt?: StsRepetitionAttemptPayload | null): StsBorgVitals => ({
  borgDyspneaPre: attempt?.borgDyspneaPre ?? null,
  borgLegPre: attempt?.borgLegPre ?? null,
  spo2Pre: attempt?.spo2Pre ?? null,
  hrPre: attempt?.hrPre ?? null,
  borgDyspneaPost: attempt?.borgDyspneaPost ?? null,
  borgLegPost: attempt?.borgLegPost ?? null,
  spo2Post: attempt?.spo2Post ?? null,
  hrPost: attempt?.hrPost ?? null,
});

const BorgCR10Field = ({
  label,
  name,
  value,
  onChange,
}: {
  label: string;
  name: string;
  value: number | null;
  onChange: (value: number | null) => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</span>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-900"
      >
        <div>
          <div className="font-semibold">{value == null ? 'Abrir escala de Borg' : `Borg ${formatBorgValue(value)}`}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {value == null ? 'Mostre a escala ao paciente e registre a resposta.' : STS_BORG_LABELS[value] || 'Valor registrado'}
          </div>
        </div>
        <span className="text-xs font-semibold text-primary-600">Ver escala</span>
      </button>

      {value != null ? (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="mt-2 text-xs text-slate-600 underline hover:text-slate-800"
        >
          Limpar valor
        </button>
      ) : null}

      {open ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-primary-600">Escala de Borg CR10</div>
                <h4 className="mt-2 text-xl font-semibold text-slate-900">{label}</h4>
                <p className="mt-1 text-sm text-slate-600">
                  Mostre a escala ao paciente, confirme a percepção e selecione abaixo o valor informado.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Fechar
              </button>
            </div>

            <div className="mt-5 overflow-x-auto">
              <div className="inline-grid min-w-full grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12">
                {STS_BORG_VALUES.map((borgValue) => (
                  <button
                    key={`${name}-${borgValue}`}
                    type="button"
                    onClick={() => {
                      onChange(borgValue);
                      setOpen(false);
                    }}
                    className={`flex min-w-[96px] flex-col items-center justify-center gap-1 rounded-2xl border p-3 text-center transition-colors ${
                      value === borgValue
                        ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <div className="text-base font-semibold leading-tight">{formatBorgValue(borgValue)}</div>
                    {STS_BORG_LABELS[borgValue] ? (
                      <div className="text-[11px] leading-tight opacity-80 whitespace-pre-line">{STS_BORG_LABELS[borgValue]}</div>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              {value != null ? (
                <button
                  type="button"
                  onClick={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Limpar valor
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const NumberField = ({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
}) => (
  <label className="text-sm">
    {label}
    <input
      type="number"
      min={min}
      max={max}
      className="mt-1 w-full rounded border px-2 py-1"
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
    />
  </label>
);

const formatCooldownLabel = (seconds: number) => {
  if (seconds <= 0) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder > 0 ? `${minutes} min ${remainder}s` : `${minutes} min`;
};

export default function STSRepeatedAttemptTestBase({
  testKey,
  title,
  description,
  durationSeconds,
  recommendedRestSeconds,
  saveLabel,
  allowFinalizeAfterFirstAttempt,
  initial = null,
  patientContext,
  onCancel,
  onDraftAttempt,
  onNext,
}: STSRepeatedAttemptTestBaseProps) {
  const normalizedInitial = normalizeStsRepetitionPayload(testKey, initial);
  const initialAttempt1 = normalizedInitial?.attempt1 ?? null;
  const initialAttempt2 = normalizedInitial?.attempt2 ?? null;
  const initialAwaitingSecondAttempt = normalizedInitial?.workflowStatus === 'awaiting_second_attempt' && !!initialAttempt1 && !initialAttempt2;
  const initialWeightKg = normalizePatientWeightKg(normalizedInitial?.patientContext?.weightKg ?? patientContext?.weightKg);

  const [chairHeightCm, setChairHeightCm] = useState<number>(
    initialAttempt1?.chairHeightCm
      ?? initialAttempt2?.chairHeightCm
      ?? STS_REFERENCE_CHAIR_HEIGHT_CM,
  );
  const [attempt, setAttempt] = useState<1 | 2>(initialAwaitingSecondAttempt ? 2 : 1);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [count, setCount] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const [attempts, setAttempts] = useState<number[]>([
    initialAttempt1?.reps ?? normalizedInitial?.attempts[0] ?? 0,
    initialAttempt2?.reps ?? normalizedInitial?.attempts[1] ?? 0,
  ]);
  const [vitals, setVitals] = useState<StsBorgVitals>(
    initialAwaitingSecondAttempt ? buildEmptyVitals() : buildVitalsFromAttempt(initialAttempt1),
  );
  const [attempt1Snapshot, setAttempt1Snapshot] = useState<StsRepetitionAttemptPayload | null>(initialAttempt1);
  const [attempt2Snapshot, setAttempt2Snapshot] = useState<StsRepetitionAttemptPayload | null>(initialAttempt2);
  const [firstAttemptSaved, setFirstAttemptSaved] = useState(Boolean(initialAttempt1));
  const [restStartAtMs, setRestStartAtMs] = useState<number | null>(null);
  const [restIntervalSeconds, setRestIntervalSeconds] = useState<number | null>(normalizedInitial?.restIntervalSeconds ?? null);
  const [decisionVisible, setDecisionVisible] = useState(initialAwaitingSecondAttempt);
  const [savingAttempt, setSavingAttempt] = useState(false);
  const [weightKg, setWeightKg] = useState<number | null>(initialWeightKg);

  const timerRef = useRef<number | null>(null);

  const bestOrNull = useMemo(() => {
    const validAttempts = attempts.filter((value) => value > 0);
    return validAttempts.length ? Math.max(...validAttempts) : null;
  }, [attempts]);
  const effectivePatientContext = useMemo(
    () => ({
      ...patientContext,
      ageYears: toFiniteNumber(patientContext?.ageYears),
      weightKg,
      heightCm: normalizePatientHeightCm(patientContext?.heightCm),
    }),
    [patientContext, weightKg],
  );

  useEffect(() => {
    if (!running) {
      if (timerRef.current != null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = window.setInterval(() => {
      setElapsed((current) => {
        const next = current + 1;
        if (next >= durationSeconds) {
          setRunning(false);
        }
        return next;
      });
    }, 1000);

    return () => {
      if (timerRef.current != null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [durationSeconds, running]);

  useEffect(() => {
    if (!running && elapsed >= durationSeconds) {
      const currentCount = Math.max(0, count);
      const index = attempt === 1 ? 0 : 1;
      setAttempts((current) => current.map((value, valueIndex) => (valueIndex === index ? currentCount : value)));

      if (attempt === 1) {
        setAttempt1Snapshot(makeSnapshot(currentCount));
        setCooldown(recommendedRestSeconds);
        setRestStartAtMs(Date.now());
        setDecisionVisible(true);
        toast.info(`${title} - primeira tentativa concluída. Revise os dados e decida se deseja repetir.`);
      } else {
        setAttempt2Snapshot(makeSnapshot(currentCount));
        toast.success('Segunda tentativa concluída. Revise e salve o teste.');
      }
    }
  }, [attempt, count, durationSeconds, elapsed, recommendedRestSeconds, running, title]);

  useEffect(() => {
    let intervalId: number | undefined;
    if (cooldown > 0) {
      intervalId = window.setInterval(() => setCooldown((current) => Math.max(0, current - 1)), 1000) as unknown as number;
    }
    return () => {
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [cooldown]);

  useEffect(() => {
    return () => {
      if (timerRef.current != null) {
        window.clearInterval(timerRef.current);
      }
    };
  }, []);

  const updateVital = <K extends keyof StsBorgVitals>(field: K, value: StsBorgVitals[K]) => {
    setVitals((current) => ({ ...current, [field]: value }));
  };

  const makeSnapshot = (reps: number): StsRepetitionAttemptPayload => ({
    reps,
    chairHeightCm,
    ...vitals,
    finishedAt: new Date().toISOString(),
  });

  const buildPayload = (workflowStatus: 'completed' | 'awaiting_second_attempt'): StsRepetitionResultPayload => {
    const attempt1 = attempt1Snapshot ?? (attempts[0] > 0 ? makeSnapshot(attempts[0]) : null);
    const attempt2 = attempt2Snapshot ?? (attempts[1] > 0 ? makeSnapshot(attempts[1]) : null);

    return {
      attempts: attempts.map((value) => Math.max(0, Math.round(value))),
      best: bestOrNull != null ? Math.round(bestOrNull) : null,
      attempt1,
      attempt2,
      restIntervalSeconds,
      workflowStatus,
      finishedAt: new Date().toISOString(),
      patientContext: effectivePatientContext,
    };
  };

  const resetFieldsForSecondAttempt = () => {
    setVitals(buildEmptyVitals());
    setElapsed(0);
    setCount(0);
  };

  const handleSaveFirstAttempt = async () => {
    if (savingAttempt) return;

    const reps1 = attempts[0] || count;
    if (reps1 <= 0) {
      toast.error('Conclua a primeira tentativa antes de salvar o rascunho.');
      return;
    }

    setSavingAttempt(true);
    try {
      const snapshot = attempt1Snapshot ?? makeSnapshot(reps1);
      const draftPayload: StsRepetitionResultPayload = {
        attempts: [Math.round(reps1), attempts[1] ?? 0],
        best: Math.round(reps1),
        attempt1: snapshot,
        attempt2: null,
        restIntervalSeconds,
        workflowStatus: 'awaiting_second_attempt',
        finishedAt: new Date().toISOString(),
        patientContext: effectivePatientContext,
      };

      if (onDraftAttempt) {
        const persisted = await onDraftAttempt(draftPayload);
        if (persisted === false) return;
      }

      setAttempt1Snapshot(snapshot);
      setFirstAttemptSaved(true);
      setDecisionVisible(true);
    } finally {
      setSavingAttempt(false);
    }
  };

  const handleBeginSecondAttempt = () => {
    if (!firstAttemptSaved) {
      const shouldContinue = window.confirm('A primeira tentativa ainda não foi salva como rascunho. Deseja iniciar a segunda assim mesmo?');
      if (!shouldContinue) return;
    }

    if (cooldown > 0) {
      const shouldContinue = window.confirm(
        `Ainda faltam ${formatCooldownLabel(cooldown)} de descanso recomendado. Deseja iniciar a segunda tentativa agora?`,
      );
      if (!shouldContinue) return;
    }

    if (restStartAtMs != null && restIntervalSeconds == null) {
      const intervalSeconds = Math.max(0, Math.round((Date.now() - restStartAtMs) / 1000));
      setRestIntervalSeconds(intervalSeconds);
    }

    setAttempt(2);
    setDecisionVisible(false);
    resetFieldsForSecondAttempt();
  };

  const handleStart = () => {
    if (attempt === 2 && cooldown > 0) {
      const shouldContinue = window.confirm(
        `Ainda faltam ${formatCooldownLabel(cooldown)} de descanso recomendado. Deseja iniciar a segunda tentativa agora?`,
      );
      if (!shouldContinue) return;
    }

    if (attempt === 2 && restStartAtMs != null && restIntervalSeconds == null) {
      const intervalSeconds = Math.max(0, Math.round((Date.now() - restStartAtMs) / 1000));
      setRestIntervalSeconds(intervalSeconds);
    }

    setElapsed(0);
    setCount(0);
    setRunning(true);
  };

  const handlePause = () => setRunning(false);

  const handleReset = () => {
    setRunning(false);
    setElapsed(0);
    setCount(0);
  };

  const canFinalSave = allowFinalizeAfterFirstAttempt ? (attempts[0] > 0 || attempts[1] > 0) && !running : attempts[1] > 0 && !running;

  const handleSaveFinal = () => {
    if (savingAttempt) return;
    onNext(buildPayload('completed'));
  };

  const shouldShowDecisionBanner = decisionVisible && attempts[0] > 0 && attempts[1] === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">{title}</p>
          <p className="text-sm opacity-90">{description}</p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <label className="text-sm">
          Altura da cadeira (cm)
          <input
            type="number"
            className="mt-1 w-full rounded border px-2 py-1"
            value={chairHeightCm}
            onChange={(event) =>
              setChairHeightCm(
                Number(event.target.value) || STS_REFERENCE_CHAIR_HEIGHT_CM,
              )
            }
          />
          <span className="mt-1 block text-xs text-slate-500">
            A comparação normativa automática usa cadeira de{' '}
            {STS_REFERENCE_CHAIR_HEIGHT_CM} cm; outra altura mantém o resultado
            sem comparação normativa automática.
          </span>
        </label>
        <div className="text-sm">
          Tentativa atual: <span className="font-semibold">{attempt}ª</span>
        </div>
        {attempt === 2 ? (
          <div className="flex items-center gap-1 text-sm md:justify-end">
            <Timer className="h-4 w-4" /> Descanso recomendado: {formatCooldownLabel(cooldown)}
          </div>
        ) : null}
      </div>

      <AssessmentWeightAdjustmentField
        value={weightKg}
        baselineValue={initialWeightKg}
        onChange={setWeightKg}
        helperText="Use quando o peso aferido nesta avaliação estiver diferente do cadastro. O ajuste é salvo apenas neste teste."
      />

      {initialAwaitingSecondAttempt ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Rascunho carregado com a 1ª tentativa salva. Revise o resumo e inicie a 2ª tentativa quando desejar.
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <BorgCR10Field
          label="Borg Dispneia (CR10) — pré"
          name={`${testKey}-borgDyspneaPre`}
          value={vitals.borgDyspneaPre}
          onChange={(value) => updateVital('borgDyspneaPre', value)}
        />
        <BorgCR10Field
          label="Borg Pernas (CR10) — pré"
          name={`${testKey}-borgLegPre`}
          value={vitals.borgLegPre}
          onChange={(value) => updateVital('borgLegPre', value)}
        />
        <NumberField label="SpO2 % (pré)" value={vitals.spo2Pre} onChange={(value) => updateVital('spo2Pre', value)} min={0} max={100} />
        <NumberField label="FC bpm (pré)" value={vitals.hrPre} onChange={(value) => updateVital('hrPre', value)} min={0} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="text-3xl tabular-nums">{String(elapsed).padStart(2, '0')}s</div>
        {!running ? (
          <button type="button" className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-white" onClick={handleStart}>
            <Play className="mr-1 h-4 w-4" /> Iniciar {durationSeconds === 30 ? '30 s' : '1 min'}
          </button>
        ) : (
          <button type="button" className="inline-flex items-center rounded-md bg-amber-600 px-3 py-2 text-white" onClick={handlePause}>
            <Pause className="mr-1 h-4 w-4" /> Pausar
          </button>
        )}
        <button type="button" className="rounded-md bg-gray-100 px-3 py-2" onClick={handleReset}>
          Zerar
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" className="rounded-md bg-gray-200 px-3 py-2" onClick={() => setCount((current) => Math.max(0, current - 1))}>
            −1
          </button>
          <div className="text-lg font-semibold tabular-nums">{count}</div>
          <button type="button" className="rounded-md bg-gray-200 px-3 py-2" onClick={() => setCount((current) => current + 1)}>
            +1
          </button>
        </div>
      </div>

      {shouldShowDecisionBanner ? (
        <div className="flex flex-col gap-3 rounded-md border border-violet-200 bg-violet-50 p-3 md:flex-row md:items-center">
          <div className="text-sm text-violet-900">
            1ª tentativa registrada (<span className="font-semibold">{attempts[0]} rep</span>). Salve o rascunho para não perder os dados e inicie a 2ª tentativa quando desejar.
          </div>
          <div className="flex gap-2 md:ml-auto">
            <button
              type="button"
              className="rounded-md bg-emerald-600 px-3 py-2 text-white disabled:opacity-50"
              onClick={() => void handleSaveFirstAttempt()}
              disabled={savingAttempt || firstAttemptSaved}
              title={firstAttemptSaved ? 'A 1ª tentativa já foi salva.' : 'Salvar 1ª tentativa'}
            >
              {firstAttemptSaved ? '1ª tentativa salva' : 'Salvar 1ª tentativa'}
            </button>
            <button type="button" className="rounded-md bg-blue-600 px-3 py-2 text-white" onClick={handleBeginSecondAttempt}>
              {cooldown > 0 ? `Iniciar 2ª (${formatCooldownLabel(cooldown)})` : 'Iniciar 2ª tentativa'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="grid gap-3 text-sm md:grid-cols-3">
        <div>
          Rep. 1ª: <span className="font-semibold">{attempts[0] || '—'}</span>
        </div>
        <div>
          Rep. 2ª: <span className="font-semibold">{attempts[1] || '—'}</span>
        </div>
        <div>
          Melhor: <span className="font-semibold">{bestOrNull ?? '—'}</span>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <BorgCR10Field
          label="Borg Dispneia (CR10) — pós"
          name={`${testKey}-borgDyspneaPost`}
          value={vitals.borgDyspneaPost}
          onChange={(value) => updateVital('borgDyspneaPost', value)}
        />
        <BorgCR10Field
          label="Borg Pernas (CR10) — pós"
          name={`${testKey}-borgLegPost`}
          value={vitals.borgLegPost}
          onChange={(value) => updateVital('borgLegPost', value)}
        />
        <NumberField label="SpO2 % (pós)" value={vitals.spo2Post} onChange={(value) => updateVital('spo2Post', value)} min={0} max={100} />
        <NumberField label="FC bpm (pós)" value={vitals.hrPost} onChange={(value) => updateVital('hrPost', value)} min={0} />
      </div>

      <div className="flex items-center justify-between pt-2">
        <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
          Cancelar
        </button>
        <button
          type="button"
          className="inline-flex items-center rounded-md bg-emerald-600 px-3 py-2 text-white disabled:opacity-50"
          disabled={!canFinalSave || savingAttempt}
          onClick={handleSaveFinal}
        >
          {saveLabel} <Check className="ml-1 h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
